// Unified cart / wishlist / compare actions + live counts.
//
// - Guest:    cart + wishlist live in localStorage; compare always localStorage.
// - Customer: cart + wishlist live in the backend; compare always localStorage.
// - On login, the guest cart + wishlist are synced to the backend then cleared.
//
// Feedback goes through the global toast (see context/ToastContext.jsx) so cart
// and wishlist messages land in the same place as every other notification.
// Consume via useShopActions().
import {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from './AuthContext.jsx';
import { useToast } from './ToastContext.jsx';

import * as cartStorage from '../services/cartStorage.js';
import * as wishlistStorage from '../services/wishlistStorage.js';
import * as compareStorage from '../services/compareStorage.js';
import * as cartApi from '../services/cartApi.js';
import * as wishlistApi from '../services/customerWishlistApi.js';
import { canAddToCart } from '../utils/productActionRules.js';
import getErrorMessage from '../utils/errorMessage.js';

const ShopActionsContext = createContext(null);

// Normalize a cart row (backend snake_case OR guest camelCase) to one shape.
function normalizeCartItem(raw) {
  const regularPrice = raw.regularPrice ?? raw.regular_price ?? null;
  const salePrice = raw.salePrice ?? raw.sale_price ?? null;
  const unit = raw.price ?? (Number(salePrice) > 0 ? salePrice : regularPrice) ?? 0;
  return {
    productId: raw.productId ?? raw.product_id,
    slug: raw.slug,
    name: raw.name,
    sku: raw.sku ?? null,
    model: raw.model ?? null,
    brand: raw.brand ?? (raw.brand_name ? { name: raw.brand_name, slug: raw.brand_slug } : null),
    image: raw.mainImage ?? raw.main_image_url ?? raw.image ?? null,
    regularPrice,
    salePrice,
    unitPrice: Number(unit) || 0,
    priceVisibility: raw.priceVisibility ?? raw.price_visibility ?? null,
    stockStatus: raw.stockStatus ?? raw.stock_status ?? null,
    quantity: Number(raw.quantity) || 1,
  };
}

const productIdOf = (p) => p?.id ?? p?.productId ?? p?.product_id;

export function ShopActionsProvider({ children }) {
  const { isAuthenticated, role } = useAuth();
  const isCustomer = isAuthenticated && role === 'customer';

  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [compareItems, setCompareItems] = useState(compareStorage.getCompareList());
  const { showToast } = useToast();

  const notify = useCallback(
    (message, tone = 'success') => showToast(message, { tone }),
    [showToast]
  );

  // ---- Loaders ----------------------------------------------------------
  const loadCart = useCallback(async () => {
    if (isCustomer) {
      try {
        const res = await cartApi.getCart();
        setCartItems((res.data?.items || []).map(normalizeCartItem));
      } catch {
        setCartItems([]);
      }
    } else {
      setCartItems(cartStorage.getCart().map(normalizeCartItem));
    }
  }, [isCustomer]);

  const loadWishlist = useCallback(async () => {
    if (isCustomer) {
      try {
        const res = await wishlistApi.getWishlist();
        setWishlistItems(res.data?.items || []);
      } catch {
        setWishlistItems([]);
      }
    } else {
      setWishlistItems(wishlistStorage.getWishlist());
    }
  }, [isCustomer]);

  const refreshCounts = useCallback(async () => {
    await Promise.all([loadCart(), loadWishlist()]);
    setCompareItems(compareStorage.getCompareList());
  }, [loadCart, loadWishlist]);

  // ---- Login sync -------------------------------------------------------
  const syncAfterLogin = useCallback(async () => {
    // Cart: merge guest items into the backend, then clear local.
    const cartPayload = cartStorage.syncCartAfterLogin();
    if (cartPayload.length) {
      try {
        await cartApi.syncCart({ items: cartPayload });
        cartStorage.clearCart();
      } catch {
        /* keep local cart if sync fails */
      }
    }
    // Wishlist: same pattern.
    const wishIds = wishlistStorage.getWishlist().map((p) => p.id).filter(Boolean);
    if (wishIds.length) {
      try {
        await wishlistApi.syncWishlist(wishIds);
        wishlistStorage.clearWishlist();
      } catch {
        /* keep local wishlist if sync fails */
      }
    }
    await refreshCounts();
  }, [refreshCounts]);

  // Clears cart + wishlist + compare everywhere (state + localStorage). Used on
  // logout so no counts linger from the previous session.
  const resetAll = useCallback(() => {
    cartStorage.clearCart();
    wishlistStorage.clearWishlist();
    compareStorage.clearCompare();
    setCartItems([]);
    setWishlistItems([]);
    setCompareItems([]);
  }, []);

  // React to auth transitions:
  //  - logout (authed -> guest):      reset all counts to zero
  //  - login as customer (guest -> customer): sync guest cart + wishlist
  //  - otherwise (initial / role change): load the appropriate source
  const prevAuthed = useRef(null);
  const prevCustomer = useRef(null);
  useEffect(() => {
    const wasAuthed = prevAuthed.current;
    const wasCustomer = prevCustomer.current;
    prevAuthed.current = isAuthenticated;
    prevCustomer.current = isCustomer;

    if (wasAuthed && !isAuthenticated) {
      resetAll();
    } else if (!wasCustomer && isCustomer) {
      syncAfterLogin();
    } else {
      refreshCounts();
    }
  }, [isAuthenticated, isCustomer, syncAfterLogin, refreshCounts, resetAll]);

  // Keep guest state in sync with direct localStorage mutations elsewhere.
  useEffect(() => {
    const onCompare = () => setCompareItems(compareStorage.getCompareList());
    const onCart = () => { if (!isCustomer) setCartItems(cartStorage.getCart().map(normalizeCartItem)); };
    const onWishlist = () => { if (!isCustomer) setWishlistItems(wishlistStorage.getWishlist()); };
    window.addEventListener(compareStorage.COMPARE_EVENT, onCompare);
    window.addEventListener(cartStorage.CART_EVENT, onCart);
    window.addEventListener(wishlistStorage.WISHLIST_EVENT, onWishlist);
    return () => {
      window.removeEventListener(compareStorage.COMPARE_EVENT, onCompare);
      window.removeEventListener(cartStorage.CART_EVENT, onCart);
      window.removeEventListener(wishlistStorage.WISHLIST_EVENT, onWishlist);
    };
  }, [isCustomer]);

  // ---- Cart actions -----------------------------------------------------
  const addToCart = useCallback(async (product, quantity = 1) => {
    const id = productIdOf(product);
    if (!id) return;
    if (isCustomer) {
      try {
        const res = await cartApi.addCartItem({ productId: id, quantity });
        setCartItems((res.data?.items || []).map(normalizeCartItem));
        notify('Product added to cart.');
      } catch (err) {
        notify(getErrorMessage(err, 'This product is not available for cart. You can request a quote.'), 'error');
      }
    } else {
      // Defensive client guard (buttons already gate on this).
      if (!canAddToCart(product)) {
        notify('This product is not available for cart. You can request a quote.', 'error');
        return;
      }
      const existed = cartStorage.getCart().some((i) => i.productId === id);
      setCartItems(cartStorage.addToCart(product, quantity).map(normalizeCartItem));
      notify(existed ? 'Cart quantity updated.' : 'Product added to cart.');
    }
  }, [isCustomer, notify]);

  const updateCartItem = useCallback(async (productId, quantity) => {
    if (isCustomer) {
      try {
        const res = await cartApi.updateCartItem(productId, { quantity });
        setCartItems((res.data?.items || []).map(normalizeCartItem));
        notify('Cart quantity updated.');
      } catch (err) {
        notify(getErrorMessage(err, 'Unable to update cart.'), 'error');
      }
    } else {
      setCartItems(cartStorage.updateCartItem(productId, quantity).map(normalizeCartItem));
      notify('Cart quantity updated.');
    }
  }, [isCustomer, notify]);

  const removeFromCart = useCallback(async (productId) => {
    if (isCustomer) {
      try {
        const res = await cartApi.removeCartItem(productId);
        setCartItems((res.data?.items || []).map(normalizeCartItem));
      } catch (err) {
        notify(getErrorMessage(err, 'Unable to update cart.'), 'error');
        return;
      }
    } else {
      setCartItems(cartStorage.removeFromCart(productId).map(normalizeCartItem));
    }
    notify('Product removed from cart.');
  }, [isCustomer, notify]);

  const clearCart = useCallback(async () => {
    if (isCustomer) {
      try {
        const res = await cartApi.clearCart();
        setCartItems((res.data?.items || []).map(normalizeCartItem));
      } catch (err) {
        notify(getErrorMessage(err, 'Unable to clear cart.'), 'error');
        return;
      }
    } else {
      cartStorage.clearCart();
      setCartItems([]);
    }
    notify('Cart cleared.');
  }, [isCustomer, notify]);

  // ---- Wishlist actions -------------------------------------------------
  const addToWishlist = useCallback(async (product) => {
    const id = productIdOf(product);
    if (!id) return;
    if (isCustomer) {
      try {
        const res = await wishlistApi.addWishlistItem(id);
        setWishlistItems(res.data?.items || []);
      } catch (err) {
        notify(getErrorMessage(err, 'Unable to update wishlist.'), 'error');
        return;
      }
    } else {
      setWishlistItems(wishlistStorage.addToWishlist(product));
    }
    notify('Product added to wishlist.');
  }, [isCustomer, notify]);

  const removeFromWishlist = useCallback(async (productId) => {
    if (isCustomer) {
      try {
        const res = await wishlistApi.removeWishlistItem(productId);
        setWishlistItems(res.data?.items || []);
      } catch (err) {
        notify(getErrorMessage(err, 'Unable to update wishlist.'), 'error');
        return;
      }
    } else {
      setWishlistItems(wishlistStorage.removeFromWishlist(productId));
    }
    notify('Product removed from wishlist.');
  }, [isCustomer, notify]);

  const isInWishlist = useCallback(
    (productId) => wishlistItems.some((p) => (p.product_id ?? p.id) === productId),
    [wishlistItems]
  );

  // ---- Compare actions (always localStorage) ----------------------------
  const addToCompare = useCallback((product) => {
    const result = compareStorage.addToCompare(product);
    setCompareItems(result.list);
    if (result.added) notify('Product added to compare.');
    else if (result.reason === 'full') notify('You can compare up to 4 products.', 'error');
    return result;
  }, [notify]);

  const removeFromCompare = useCallback((productId) => {
    setCompareItems(compareStorage.removeFromCompare(productId));
    notify('Product removed from compare.');
  }, [notify]);

  const isInCompare = useCallback(
    (productId) => compareItems.some((p) => (p.id ?? p.productId) === productId),
    [compareItems]
  );

  const cartCount = cartItems.reduce((n, i) => n + (Number(i.quantity) || 0), 0);

  const value = {
    cartItems,
    wishlistItems,
    compareItems,
    cartCount,
    wishlistCount: wishlistItems.length,
    compareCount: compareItems.length,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    addToCompare,
    removeFromCompare,
    isInCompare,
    syncAfterLogin,
    refreshCounts,
    notify,
  };

  return (
    <ShopActionsContext.Provider value={value}>
      {children}
    </ShopActionsContext.Provider>
  );
}

export function useShopActions() {
  const ctx = useContext(ShopActionsContext);
  if (!ctx) throw new Error('useShopActions must be used inside <ShopActionsProvider>');
  return ctx;
}
